/*!
 * Copyright (c) Trimble Inc. All rights reserved
 */

import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Inject,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { Observable, Subject, Subscription, of } from 'rxjs';
import { SystemState } from '../../models/system-state';
import { GlobalConstants } from '../../constants/global-constants';
import {
  ISelectSystemFacade,
  SELECT_SYSTEM_FACADE_TOKEN,
} from '../../../../../data-access/src/lib/domain/facades-models/select-system-facade-interface';
import { SystemInfo } from 'libs/sysque/toolbelt/data-access/src/lib/domain/system-info';
import { SysqueSelectBoxComponent } from '../../../../../../shared/ui/src/lib/components/sysque-select-box';
import { getMaxContentWidth } from '../../../../../../shared/ui/src/lib/utils/common.utils';

@Component({
  selector: 'sysque-select-system',
  templateUrl: './select-system.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class SelectSystemComponent implements OnInit, OnDestroy {
  @Input() systemInfo$: Observable<SystemInfo[]> = of([]);
  @Input() selectedSystem: string;
  @Input() panelName: string;
  @Output() valueChanged: EventEmitter<any> = new EventEmitter<any>();
  @ViewChild(SysqueSelectBoxComponent) selectBox: SysqueSelectBoxComponent;
  @ViewChild('parentContainer', { static: false }) parentContainer: ElementRef;

  public systemList: SystemInfo[] = [];
  public calculatedWidth: string = 'auto'; // Dynamically calculated width
  public resizeObserver: ResizeObserver;

  public disabled = false;
  public key = 'name';
  public showClearButton = false;
  public placeholder = 'System';
  public isManageMode$: Observable<boolean>;
  public width = '323px';
  public height = '30px';
  public className = 'select-system-box';
  public isManageModeSubscription: Subscription;
  public systemState: SystemState;

  constructor(
    @Inject(SELECT_SYSTEM_FACADE_TOKEN) private selectSystemFacade: ISelectSystemFacade,
    private cd: ChangeDetectorRef
  ) {}

  public ngOnInit(): void {
    this.isManageMode$ = this.selectSystemFacade.selectManageMode();
    this.isManageModeSubscription = this.isManageMode$.subscribe((data) => {
      this.width = data ? '490px' : '323px';
    });

    window.addEventListener('storage', this.handleStorageEvent.bind(this));
    this.systemState = this.selectSystemFacade.getSystemState();
    this.selectedSystem = this.systemState?.selectedSystem ?? '';
    this.systemList = this.systemState.systemList;
    this.setWidth();
  }

  public ngAfterViewInit(): void {
    this.setupResizeObserver();
    this.calculateMaxWidth();
  }

  public ngOnDestroy(): void {
    if (this.isManageModeSubscription) {
      this.isManageModeSubscription.unsubscribe();
    }
    window.removeEventListener('storage', this.handleStorageEvent.bind(this));
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }
  }

  public setWidth(): void {
    switch (this.panelName) {
      case 'addFittingPanel':
        this.width = '323px';
        break;
      case 'manageModePanel':
        this.width = '490px';
        break;
      case 'systemPanel':
        this.width = '-webkit-fill-available';
        break;
      default:
        this.width = '323px';
        break;
    }
  }

  public onValueChanged(e: any): void {
    this.calculateMaxWidth();
    this.valueChanged.emit(e);
  }

  public handleStorageEvent(event: StorageEvent): void {
    if (event.key === GlobalConstants.SYSTEM_STATE) {
      const data = JSON.parse(event.newValue);
      if (this.selectedSystem !== data.selectedSystem) {
        this.selectedSystem = data.selectedSystem;
      }
    }

    if (event.key === GlobalConstants.SYSQUE_ELECTRICAL_STATE_KEY) {
      const data = JSON.parse(event.newValue);
      this.systemList = data.systemList;
      this.calculateMaxWidth();
      this.cd.detectChanges();
    }
  }

  private setupResizeObserver(): void {
    if (typeof ResizeObserver === 'undefined') {
      return;
    }
    this.resizeObserver = new ResizeObserver(() => {
      this.calculateMaxWidth();
      this.cd.detectChanges(); // Ensure Angular detects the changes
    });

    if (this.parentContainer && this.parentContainer.nativeElement) {
      this.resizeObserver.observe(this.parentContainer.nativeElement);
    }
  }

  public calculateMaxWidth(): void {
    // Recalculate width based on content and container size
    if (this.panelName === 'systemPanel' && this.systemList && this.systemList.length > 0) {
      this.calculatedWidth = this.getMaxContentWidth(this.systemList.map((x) => x.name));
    }
  }

  private getMaxContentWidth(items: string[]): string {
    let maxWidth = Number(getMaxContentWidth(items));

    const selectBoxWidth = this.selectBox?.getSelectBoxWidth();

    if (maxWidth > selectBoxWidth) {
      return (selectBoxWidth + 200).toString();
    }

    // Return the maximum width with some padding
    return (maxWidth + 30).toString(); // Add padding if necessary
  }
}
